// PaymentGateway.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useNavigate, useParams } from "react-router-dom";
import CaseApi from "Service/Case.api";
import { formateCaseData } from "./Formatter";
import {
  CaseDataType,
  // ZohoPaymentOptions,
} from "./../../../../../DataTypes/Case.type";
import PaymentApi from "Service/Payment.api";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../../../../Redux/Screen/Action";
import { RouteUrls } from "Constants/RouteUrls";

const PaymentGateway = () => {
  const { caseId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [caseData, setCaseData] = useState<CaseDataType | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);

  useEffect(() => {
    handleOnload();
    const script = document.createElement("script");
    const timestamp = new Date().getTime();
    script.src = `https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js?t=${timestamp}`; // Add timestamp to bust cache
    script.async = true;
    script.onload = () => console.log("Fresh Zoho SDK Loaded");
    script.onerror = () => {
      dispatch(
        setAlertData({
          alertMessage: "Failed to load Zoho Payments SDK.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    };
    document.body.appendChild(script);

    return () => {
      if ((window as any).ZPayments) {
        delete (window as any).ZPayments;
      }
      const existingScript = document.querySelector(`script[src*="zpayments.js"]`);
      if (existingScript) existingScript.remove();
      
      const zohoOverlays = document.querySelectorAll('[id^="zpay-"], [class^="zpay-"]');
      zohoOverlays.forEach(el => el.remove());
    };
  }, [caseId]);

  const handleOnload: () => void = async () => {
    const res = await CaseApi.fetchCaseById({ caseId: caseId });
    const orderId = res?.data?.orderTypeId;
    setOrderId(orderId);
    const formattedData = formateCaseData(res?.data);
    setCaseData(formattedData);
  };

  const openZohoWidget = useCallback(
    async (SessionData: {
      sessionId: string;
      amount: number;
      invoiceNumber: string;
      refrenceNumber: string;
      caseId: string;
      user: {
        customerName: string;
        customerEmail: string;
        customerPhone: string;
        customerId: string;
      };
    }) => {
      let instance = null;

      try {
        if (!window.ZPayments) {
          dispatch(
            setAlertData({
              alertMessage: "Zoho SDK not loaded.",
              alertVariant: "error",
              openAlert: true,
            })
          );
          return;
        }
        const config = {
          account_id: process.env.REACT_APP_ZOHO_ACCOUNT_ID,
          domain: process.env.REACT_APP_ZOHO_DOMAIN,
          otherOptions: {
            api_key: process.env.REACT_APP_ZOHO_API_KEY,
            is_test_mode: true
          },
        };

        const options = {
          amount: String(SessionData.amount),
          currency_code: "INR",         
          payments_session_id: SessionData.sessionId,
          currency_symbol: "₹",
          business: process.env.REACT_APP_BUSINESS_NAME,
          description: caseData?.caseTypeName,
          invoice_number: SessionData.invoiceNumber,
          reference_number: SessionData.refrenceNumber,
          redirect_url: `${window.location.origin}/payment-status?status=success`,
          cancel_url: `${window.location.origin}/payment-status?status=cancel`,
          address: {
            name: SessionData.user.customerName,
            email: SessionData.user.customerEmail,
            phone: SessionData.user.customerPhone,
          },
        };

        // Create instance WITH config
        instance = new (window.ZPayments as any)(config);

        // Open widget
        const result = await instance.requestPaymentMethod(options);
        if (result?.message === "Payment successful!") {
          dispatch(
            setAlertData({
              alertMessage: result?.message ?? "Payment Successful!",
              alertVariant: "success",
              openAlert: true,
            })
          );
          await instance.close();
          navigate(`/chat/${orderId}`);
        } else {
          await instance.close();
          dispatch(
            setAlertData({
              alertMessage: "Payment Failed",
              alertVariant: "error",
              openAlert: true,
            })
          );
        }
      } catch (err: any) {
        await instance.close();
        console.error(err, "error in payment");
        if (err.code === "widget_closed") {
          dispatch(
            setAlertData({
              alertMessage: "User closed the payment widget.",
              alertVariant: "info",
              openAlert: true,
            })
          );
        } else {
          dispatch(
            setAlertData({
              alertMessage: "Payment error",
              alertVariant: "error",
              openAlert: true,
            })
          );
        }
      } finally {
        await instance.close();
        dispatch(stopLoading());
      }
    },
    [dispatch, navigate, orderId]
        // [caseData, myInfo]
  );
  const handleCreateSession: () => void = async () => {
    dispatch(
      startLoading({ loading: true, loadtext: "Redirecting to Payment..." })
    );
    const payload = {
      caseId: caseId,
    };
    const res = await CaseApi.createPaymentSession(payload);

    if (res?.success) {
      const mapMetadata = (
        metaDataArray: Array<{ key: string; value: any }> = []
      ) => {
        return metaDataArray.reduce((acc: Record<string, any>, item) => {
          acc[item.key] = item?.value;
          return acc;
        }, {} as Record<string, any>);
      };
      const metadata = mapMetadata(res?.data?.payments_session?.meta_data);
      dispatch(stopLoading());
      await openZohoWidget({
        sessionId: res?.data?.payments_session?.payments_session_id,
        amount: Number(res?.data?.payments_session?.amount),
        invoiceNumber: res?.data?.payments_session?.invoice_number,
        refrenceNumber: res?.data?.payments_session?.reference_number,
        caseId: caseId ?? "",
        user: {
          customerName: metadata.customer_name,
          customerEmail: metadata.customer_email,
          customerPhone: metadata.customer_phone,
          customerId: metadata.customer_id,
        },
      });
    } else {
      dispatch(stopLoading());
    }
  };

  const handlePayment: () => void = async () => {
    dispatch(
      startLoading({ loading: true, loadtext: "Payment in Progress..." })
    );
    const payload = {
      clientId: myInfo.id,
      caseTypeId: caseData?.caseTypeId,
      orderTypeId: caseData?.orderTypeId,
      description: `Payment for ${caseData?.caseTypeName}`,
      caseId: caseId,
    };
    const res = await PaymentApi.initiatePayment(payload);
    if (res?.success) {
      dispatch(stopLoading());
      dispatch(
        setAlertData({
          alertMessage: "Payment Successfull",
          alertVariant: "success",
          openAlert: true,
        })
      );
      navigate(`../../../${RouteUrls.dashboard}`);
    } else {
      dispatch(stopLoading());
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Card
        sx={{
          boxShadow: 6,
          borderRadius: 4,
          p: 3,
          backgroundColor: "#fefefe",
        }}
      >
        <CardContent>
          <Box textAlign="center">
            <Typography
              variant="h5"
              fontWeight={600}
              color="primary"
              gutterBottom
            >
              {caseData?.caseTypeName}
            </Typography>
            <Typography variant="body1" color="primary" gutterBottom>
              Order Type: {caseData?.orderTypeName}
            </Typography>

            <Typography variant="h4" sx={{ mt: 4 }} color="primary">
              ₹{caseData?.finalCasePrice}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 5, px: 5 }}
              startIcon={<CreditCardIcon />}
              onClick={handleCreateSession}
            >
              Pay Now
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentGateway;
